<script setup lang="ts">
import { keepPreviousData, useQueryClient } from '@tanstack/vue-query'
import { computed, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getUserStatisticsAction } from '@/api/functions/users/get-user-statistics.action'
import { getUsersAction } from '@/api/functions/users/get-users.action'
import { Filters } from '@/components'
import { useQueryApi } from '@/composables/useApi'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import { USERS_LIST_SCROLL_KEY } from '@/constants/user-list.constant.ts'
import CreateUserDrawer from './partials/CreateUserDrawer.vue'
import PendingUsers from './partials/PendingUsers.vue'
import UserCard from './partials/UserCard.vue'
import UserStatusCard from './partials/UserStatusCard.vue'

const router = useRouter()
const route = useRoute()
const queryClient = useQueryClient()
const currentPage = computed(() => Number(route.query.page) || 1)
const activeFilters = computed<Record<string, unknown>>(() => {
  const { page, ...filters } = route.query
  return filters
})

const { userStatusOptions, referralOptions, countries } = useGeneralSettings()

const {
  data: usersData,
  isLoading: isUsersLoading,
  // isError: isUsersError,
  // refetch: refetchUsers,
} = useQueryApi({
  queryKey: computed(() => ['users', currentPage.value, activeFilters.value]),
  queryFn: () => {
    const filters = Object.fromEntries(
      Object.entries(activeFilters.value).filter(([key, value]) => key !== 'page' && value !== false)
    )
    return getUsersAction({
      page: currentPage.value,
      ...filters,
    })
  },
  placeholderData: keepPreviousData,

  staleTime: 1000 * 60 * 3,
})

//  Fetch user statistics
const { data: statistics } = useQueryApi({
  queryKey: ['usersStatistics'],
  queryFn: getUserStatisticsAction,
  staleTime: 1000 * 60 * 5,
})

const requisitionsStatus = computed(() => {
  return statistics.value?.data.flatMap((item) => ({
    label: item.label,
    price: item.value,
    currency: 'COUNT',
  }))
})

const users = computed(() => usersData?.value?.data || [])
const pagination = computed(() => usersData?.value?.pagination)
const totalItems = computed(() => pagination.value?.total || 0)
watch(
  users,
  async (list) => {
    if (!list.length) return
    const id = sessionStorage.getItem(USERS_LIST_SCROLL_KEY)
    if (!id) return
    await nextTick()
    const el = document.getElementById(`user-${id}`)
    if (el) {
      el.scrollIntoView({ block: 'center' })
    }
    sessionStorage.removeItem(USERS_LIST_SCROLL_KEY)
  },
  { immediate: true }
)

// const requisitionsStatus = computed(() => [
//   {
//     label: 'تعداد کل کاربران ثبت نام کرده',
//     price: stats?.value?.total_users ?? 0,
//     currency: 'COUNT',
//   },
//   {
//     label: 'تعداد کل کاربران تایید شده',
//     price: stats?.value?.total_verified_users ?? 0,
//     currency: 'COUNT',
//   },
//   {
//     label: 'تعداد کاربران ثبت نام کرده این ماه',
//     price: stats?.value?.total_users_within_current_month ?? 0,
//     currency: 'COUNT',
//   },
//   {
//     label: 'تعداد کاربران ثبت نام اولیه این ماه',
//     price: stats?.value?.total_users_with_email_verification_pending_status_within_current_month ?? 0,
//     currency: 'COUNT',
//   },
//   {
//     label: 'تعداد کاربران مورد تایید این ماه',
//     price: stats?.value?.total_users_with_verified_status_within_current_month ?? 0,
//     currency: 'COUNT',
//   },
//   {
//     label: 'تعداد کاربران در انتظار تایید شماره موبایل این ماه',
//     price: stats?.value?.total_users_with_mobile_verification_pending_status_within_current_month ?? 0,
//     currency: 'COUNT',
//   },
//   {
//     label: 'تعداد کاربران در انتظار آپلود مدارک هویتی این ماه',
//     price: stats?.value?.total_users_with_identification_registration_pending_status_within_current_month ?? 0,
//     currency: 'COUNT',
//   },
//   {
//     label: 'تعداد کاربران در انتظار تایید مدارک هویتی این ماه',
//     price: stats?.value?.total_users_with_identification_verification_pending_status_within_current_month ?? 0,
//     currency: 'COUNT',
//   },
// ])

//  Filter Fields
const filterFields = computed(() => [
  {
    type: 'input',
    key: 'code_or_name',
    label: ' کد کاربری یا نام کاربری را وارد کنید',
    title: 'جستجو',
  },
  {
    type: 'select',
    key: 'status',
    label: 'انتخاب وضعیت ثبت‌نام',
    title: 'وضعیت ثبت‌نام',
    options: userStatusOptions.value,
  },
  {
    type: 'select',
    key: 'referral',
    label: 'انتخاب کانال ورودی',
    title: 'کانال ورودی',
    options: referralOptions.value,
  },
  {
    type: 'date',
    key: 'requestDate',
    title: 'تاریخ ثبت‌نام',
    rangeKeys: {
      min: 'from_date',
      max: 'till_date',
    },
  },
  {
    type: 'select',
    key: 'country',
    label: 'انتخاب کشور کاربر',
    title: 'کشور کاربر',
    options: countries.value,
  },
  {
    type: 'checkbox',
    key: 'invited_user',
    label: 'نمایش کاربران معرفی شده',
  },
  {
    type: 'checkbox',
    key: 'is_old',
    label: 'نمایش کاربران قدیمی',
  },
  {
    type: 'input',
    key: 'mobile',
    label: 'شماره همراه',
    title: 'جستجو',
  },
  {
    type: 'input',
    key: 'email',
    label: 'ایمیل',
    title: 'جستجو',
  },
])

const onApplyFilters = (values: Record<string, string>) => {
  const query = Object.fromEntries(Object.entries(values).filter(([, v]) => v))
  router.push({ path: route.path, query: { ...query, page: 1 } })
}

const onResetFilters = () => {
  router.push({ path: route.path, query: {} })
}

const goToPage = (page: number) => {
  router.push({ path: route.path, query: { ...route.query, page } })
}

const refetchUsers = () => {
  queryClient.invalidateQueries({ queryKey: ['users'] })
}
</script>

<template>
  <div class="w-full mb-12">
    <div class="w-full grid grid-cols-1 md:grid-cols-4 gap-6">
      <template v-for="(stat, index) in requisitionsStatus" :key="`stat-item-${index}`">
        <UserStatusCard :data="stat" />
      </template>
    </div>

    <div class="flex items-end justify-between">
      <h2 class="text-[22px] text-[#1b325a ] mt-8">لیست کاربران</h2>
      <CreateUserDrawer />
    </div>

    <!-- 👉 Filters Section -->
    <Filters :fields="filterFields" @apply="onApplyFilters" @reset="onResetFilters" class="-mt-1" />

    <!-- 👉 Pending Users -->
    <PendingUsers />

    <!-- 👉 Main Cards && Pagination  -->
    <div v-if="isUsersLoading" class="text-center py-8 text-gray-500">
      در حال دریافت اطلاعات کاربران...
    </div>
    <div v-else>
      <div v-if="users.length > 0" class="mt-8 flex flex-col gap-6">
        <UserCard v-for="user in users" :key="user.uuid" :user="user" @confirmUser="refetchUsers" />

        <Pagination :currentPage="currentPage" :itemsPerPage="pagination?.perPage" :totalItems="totalItems"
          @update:currentPage="goToPage" :isAdmin="true" />
      </div>
      <EmptyState v-else :onReset="onResetFilters" />
    </div>
  </div>
</template>
